import React, { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { clearStripeCaches, createDirectCheckoutSession } from '@/lib/stripe';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Directly redirect to Stripe when the component mounts
  useEffect(() => {
    if (isOpen && currentUser) {
      redirectToStripe();
    } else if (isOpen && !currentUser) {
      setError('You must be logged in to make a purchase.');
    }
  }, [isOpen, currentUser]);

  const redirectToStripe = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Clear any Stripe-related caches to ensure we get fresh data
      clearStripeCaches();

      // Ensure user is logged in
      if (!currentUser) {
        setError('You must be logged in to make a purchase.');
        setIsLoading(false);
        return;
      }

      // Double check that we have a valid user with a UID
      if (!currentUser.uid) {
        setError('Your login session appears to be incomplete. Please sign out and sign in again.');
        setIsLoading(false);
        return;
      }

      // Force token refresh to ensure we have a fresh token
      let idToken = '';
      try {
        idToken = await currentUser.getIdToken(true);
      } catch (tokenError) {
        // Continue anyway - we'll pass the userId explicitly
      }

      // Get the current domain for success/cancel URLs
      const domain = window.location.origin;

      // Create a data object with the essential fields
      // Add a timestamp to prevent caching issues
      const paymentData = {
        domain,
        userId: currentUser.uid,
        timestamp: Date.now(), // Add timestamp as cache-busting parameter
      };

      // Try direct fetch first (more reliable than callable functions)
      try {
        // Call the HTTP function directly with Authorization header and userId in both header and body
        const response = await fetch(
          'https://us-central1-vibecraft-5c54c.cloudfunctions.net/createCheckoutSessionHttp',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: idToken ? `Bearer ${idToken}` : '',
              'X-User-ID': currentUser.uid, // Add user ID as a header as well
            },
            body: JSON.stringify(paymentData),
          }
        );

        // Log the response status for debugging

        // If not OK, try to get the error message from the response
        if (!response.ok) {
          const errorText = await response.text();

          throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const result = await response.json();

        // Redirect to Stripe Checkout
        if (result && result.url) {
          // Add a cache-busting parameter to the URL
          const url = new URL(result.url);
          url.searchParams.append('_cb', Date.now().toString());
          window.location.href = url.toString();
          return;
        } else {
          throw new Error('No checkout URL returned from server');
        }
      } catch (httpError) {
        // Fall back to the callable function
        try {
          // Initialize Firebase Functions
          const functions = getFunctions();
          const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

          // Call the Cloud Function with explicit userId
          const result = await createCheckoutSession({
            ...paymentData,
            userId: currentUser.uid, // Ensure userId is included
          });

          // Extract the URL from the result
          const data = result.data as { url: string };

          // Redirect to Stripe Checkout
          if (data && data.url) {
            // Add a cache-busting parameter to the URL
            const url = new URL(data.url);
            url.searchParams.append('_cb', Date.now().toString());
            window.location.href = url.toString();
          } else {
            throw new Error('No checkout URL returned from server');
          }
        } catch (callableError) {
          throw callableError;
        }
      }
    } catch (error: any) {
      // Extract the Firebase error message if available
      let errorMessage = 'There was an error processing your payment request. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      }
      if (error.details) {
        errorMessage = error.details;
      }

      // Log detailed error information

      // Show a toast with the error
      toast({
        title: 'Payment Error',
        description:
          'There was an issue connecting to our payment system. Please try again or contact support if the problem persists.',
        variant: 'destructive',
      });

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // Show an error dialog if there's an error
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-center">{error}</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show a loading dialog while processing
  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Loading Payment Page</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p>Preparing...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // This component doesn't render anything visible when not loading or showing an error
  // It just handles the redirect logic
  return null;
};

export default PaymentModal;
